import {
  USER_ACTIONS_HISTORY_DRAWER_DEDUPLICATE_WINDOW_MS,
  USER_ACTIONS_HISTORY_MAX_STORED_ITEMS,
  USER_ACTIONS_HISTORY_STORAGE_KEY_PREFIX,
} from "../../constants/common.const";
import { CRUD_ROUTE_ENTITY_DRAWER_TYPES } from "../../constants/drawers.constants";
import { EditFormMode } from "../../enums";
import { RydeUiUnit } from "../../models/common.models";
import { DrawerProps, DrawerType } from "../../models/drawers.models";
import {
  IUserActionHistoryEntry,
  IUserActionsHistoryStored,
  UserActionHistoryOrigin,
} from "../../models/user-actions-history.models";
import { normalizePositiveIntId } from "../numbers.helpers";
import { emptyStore, newEntryId } from "./user-actions-storage.utils";

const editFormModeFromPayloadOrCrudRoute = (
  p: Record<string, unknown>,
): EditFormMode => {
  const m = p.mode;
  const allowed = Object.values(EditFormMode) as string[];
  if (typeof m === "string" && allowed.includes(m)) {
    return m as EditFormMode;
  }
  return EditFormMode.CrudRoute;
};

const resolveHistoryCrudEntityFromPayload = (
  drawerType: DrawerType,
  payload: Record<string, unknown>,
): RydeUiUnit | null => {
  switch (drawerType) {
    case DrawerType.EditForm: {
      const entity = payload.entity;
      if (typeof entity === "string" && entity !== "" && entity !== "unknown") {
        return entity as RydeUiUnit;
      }
      return null;
    }
    case DrawerType.RideEditForm:
      return "ride";
    case DrawerType.JourneyEditForm:
      return "journey";
    case DrawerType.BookingEditForm:
      return "booking";
    case DrawerType.CourseEditForm:
      return "course";
    case DrawerType.StationRouteEditForm:
      return "stationRoute";
    case DrawerType.NotificationEditForm:
      return "notification";
    default:
      return null;
  }
};

export const getUserActionsHistoryStorageKey = ({
  userId,
  companyId,
}: {
  userId: number;
  companyId: number;
}): string =>
  `${USER_ACTIONS_HISTORY_STORAGE_KEY_PREFIX}${userId}_${companyId}`;

export const readUserActionsHistory = ({
  userId,
  companyId,
}: {
  userId: number;
  companyId: number;
}): IUserActionsHistoryStored => {
  if (typeof window === "undefined") {
    return emptyStore();
  }
  const raw = window.localStorage.getItem(
    getUserActionsHistoryStorageKey({ userId, companyId }),
  );
  if (!raw) {
    return emptyStore();
  }
  try {
    const parsed = JSON.parse(raw) as IUserActionsHistoryStored;
    if (parsed?.v !== 1 || !Array.isArray(parsed.items)) {
      return emptyStore();
    }
    const items = parsed.items.filter(
      (item): item is IUserActionHistoryEntry =>
        item.origin === UserActionHistoryOrigin.Drawer ||
        item.origin === UserActionHistoryOrigin.RouteCard,
    );
    return {
      v: 1,
      items: items.slice(0, USER_ACTIONS_HISTORY_MAX_STORED_ITEMS),
    };
  } catch {
    return emptyStore();
  }
};

export const writeUserActionsHistory = ({
  userId,
  companyId,
  data,
}: {
  userId: number;
  companyId: number;
  data: IUserActionsHistoryStored;
}): void => {
  if (typeof window === "undefined") {
    return;
  }
  const key = getUserActionsHistoryStorageKey({ userId, companyId });
  const payload = JSON.stringify(data);
  // setItem throws DOMException (e.g. QuotaExceededError) when per-origin storage is full or the value is too large.
  try {
    window.localStorage.setItem(key, payload);
  } catch {
    const reduceDivider = 2;
    // Retry with fewer entries so the serialized payload fits; limits vary by browser and disk pressure.
    const trimmed: IUserActionsHistoryStored = {
      v: data.v,
      items: data.items.slice(0, Math.floor(data.items.length / reduceDivider)),
    };
    try {
      window.localStorage.setItem(key, JSON.stringify(trimmed));
    } catch {
      // Last resort: remove this key so future writes do not throw and the app keeps working without history.
      window.localStorage.removeItem(key);
    }
  }
};

export const appendUserActionEntry = ({
  userId,
  companyId,
  partial,
}: {
  userId: number;
  companyId: number;
  partial: Partial<IUserActionHistoryEntry>;
}): void => {
  if (
    partial.origin === undefined ||
    partial.title === undefined ||
    partial.pathname === undefined ||
    partial.search === undefined
  ) {
    return;
  }

  const historyEntry: IUserActionHistoryEntry = {
    id: partial.id ?? newEntryId(),
    recordedAt: partial.recordedAt ?? Date.now(),
    origin: partial.origin,
    title: partial.title,
    pathname: partial.pathname,
    search: partial.search,
    drawerType: partial.drawerType,
    drawerPayload: partial.drawerPayload,
  };

  const data = readUserActionsHistory({ userId, companyId });
  const items = [...data.items];

  if (
    historyEntry.origin === UserActionHistoryOrigin.RouteCard &&
    items[0]?.origin === UserActionHistoryOrigin.RouteCard &&
    items[0].pathname === historyEntry.pathname &&
    items[0].search === historyEntry.search
  ) {
    return;
  }

  if (
    historyEntry.origin === UserActionHistoryOrigin.Drawer &&
    items[0]?.origin === UserActionHistoryOrigin.Drawer &&
    items[0].drawerType === historyEntry.drawerType &&
    JSON.stringify(items[0].drawerPayload) ===
      JSON.stringify(historyEntry.drawerPayload) &&
    historyEntry.recordedAt - items[0].recordedAt <
      USER_ACTIONS_HISTORY_DRAWER_DEDUPLICATE_WINDOW_MS
  ) {
    return;
  }

  const nextItems = [historyEntry, ...items].slice(
    0,
    USER_ACTIONS_HISTORY_MAX_STORED_ITEMS,
  );
  writeUserActionsHistory({
    userId,
    companyId,
    data: { v: 1, items: nextItems },
  });
};

export const clearUserActionsHistory = ({
  userId,
  companyId,
}: {
  userId: number;
  companyId: number;
}): void => {
  writeUserActionsHistory({
    userId,
    companyId,
    data: emptyStore(),
  });
};

/** Drawer stack opens and full-page CRUD cards. */
export const filterToDrawerHistoryItems = (
  items: IUserActionHistoryEntry[],
): IUserActionHistoryEntry[] =>
  items.filter(
    e =>
      (e.origin === UserActionHistoryOrigin.Drawer && e.drawerType) ||
      e.origin === UserActionHistoryOrigin.RouteCard,
  );

/** Drawer props for `openDrawer` from persisted history (shape matches sanitize at record time). */
export const buildDrawerReopenPayload = (
  drawerType: DrawerType,
  drawerPayload: Record<string, unknown> | undefined,
): DrawerProps<DrawerType> => {
  const base = { ...(drawerPayload ?? {}) };
  delete base.type;
  delete base.drawerNumber;
  delete base.resolveFn;
  delete base.onCloseCustomAction;
  return { type: drawerType, ...base } as DrawerProps<DrawerType>;
};

/**
 * Props for `clearCrudEditDrawerAtomsBeforeOpen` when navigating to a full-page CRUD route.
 * Uses the same `type` as in history (`resolveCrudEditDrawerAtomKey` branches on `props.type`).
 */
export const buildCrudRouteEditFormClearPropsFromHistoryEntry = (
  entry: IUserActionHistoryEntry,
): DrawerProps<DrawerType> | null => {
  if (entry.origin !== UserActionHistoryOrigin.RouteCard || !entry.drawerType) {
    return null;
  }
  const dt = entry.drawerType;
  if (!CRUD_ROUTE_ENTITY_DRAWER_TYPES.has(dt)) {
    return null;
  }
  const p = entry.drawerPayload ?? {};
  const id = normalizePositiveIntId(p.id);
  const idCopyFrom = normalizePositiveIntId(p.idCopyFrom);
  if (id === undefined && idCopyFrom === undefined) {
    return null;
  }

  const mode = editFormModeFromPayloadOrCrudRoute(p);

  switch (dt) {
    case DrawerType.EditForm: {
      const entity = resolveHistoryCrudEntityFromPayload(dt, p);
      if (entity === null) {
        return null;
      }
      return {
        type: DrawerType.EditForm,
        entity,
        id,
        idCopyFrom,
        mode: EditFormMode.CrudRoute,
      };
    }
    case DrawerType.RideEditForm:
      return { type: DrawerType.RideEditForm, id, idCopyFrom };
    case DrawerType.BookingEditForm:
      return {
        type: DrawerType.BookingEditForm,
        id,
        idCopyFrom,
        mode,
      };
    case DrawerType.CourseEditForm:
      return {
        type: DrawerType.CourseEditForm,
        id,
        idCopyFrom,
        mode,
      };
    case DrawerType.JourneyEditForm:
      return {
        type: DrawerType.JourneyEditForm,
        id,
        idCopyFrom,
        mode,
      };
    case DrawerType.StationRouteEditForm:
      return {
        type: DrawerType.StationRouteEditForm,
        id,
        idCopyFrom,
        mode,
      };
    case DrawerType.NotificationEditForm:
      return {
        type: DrawerType.NotificationEditForm,
        id,
        idCopyFrom,
        mode,
      };
    default:
      return null;
  }
};

export const getRecordAvailabilityParamsForHistoryEntry = (
  entry: IUserActionHistoryEntry,
): { entity: RydeUiUnit; recordId: number } | null => {
  if (!entry.drawerType) {
    return null;
  }

  const supportsAvailabilityCheck =
    entry.origin === UserActionHistoryOrigin.Drawer ||
    entry.origin === UserActionHistoryOrigin.RouteCard;

  if (!supportsAvailabilityCheck) {
    return null;
  }

  const p = entry.drawerPayload ?? {};
  const id = typeof p.id === "number" ? p.id : undefined;
  const idCopyFrom =
    typeof p.idCopyFrom === "number" ? p.idCopyFrom : undefined;
  const recordId = id ?? idCopyFrom;

  if (!recordId || recordId < 1) {
    return null;
  }

  const entity = resolveHistoryCrudEntityFromPayload(entry.drawerType, p);
  if (entity === null) {
    return null;
  }
  return { entity, recordId };
};
